import React, { useState, useCallback, useEffect } from 'react';
import ProjectManager from '../../components/ProjectManager/ProjectManager';
import { getLanguageTranslations } from '../../utilities/translations';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const [currentProject, setCurrentProject] = useState(null);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('app_language') || 'ar';
  });
  const t = getLanguageTranslations(language);

  // استمع لتغييرات اللغة
  useEffect(() => {
    const handleStorageChange = () => {
      const newLang = localStorage.getItem('app_language') || 'ar';
      setLanguage(newLang);
    };

    window.addEventListener('storage', handleStorageChange);
    
    const interval = setInterval(() => {
      const currentLang = localStorage.getItem('app_language') || 'ar';
      if (currentLang !== language) {
        setLanguage(currentLang);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [language]);

  // إنشاء مشروع جديد
  const handleProjectCreate = useCallback((project) => {
    // إنشاء بنية البيانات الافتراضية للمشروع الجديد
    const defaultProjectData = {
      pagesById: {
        floor: {
          id: 'floor',
          label: t.floor || 'أرضية',
          kind: 'floor',
          elements: [],
          layers: {
            layer1: { name: t.baseLayer || 'الطبقة الأساسية', visible: true, color: '#3498db' },
            layer2: { name: t.linesLayer || 'طبقة الخطوط', visible: true, color: '#e74c3c' },
            layer3: { name: t.textLayer || 'طبقة النصوص', visible: true, color: '#FF9800' },
            measurements: { name: t.measurementsLayer || 'طبقة القياسات', visible: true, color: '#9b59b6' }
          },
          selectedLayer: 'layer1',
          history: [[]],
          historyIndex: 0,
          zoom: 1,
          pan: { x: 0, y: 0 }
        },
        ceiling: {
          id: 'ceiling',
          label: t.ceiling || 'سقف',
          kind: 'ceiling',
          elements: [],
          layers: {
            layer1: { name: t.baseLayer || 'الطبقة الأساسية', visible: true, color: '#3498db' },
            layer2: { name: t.linesLayer || 'طبقة الخطوط', visible: true, color: '#e74c3c' },
            layer3: { name: t.textLayer || 'طبقة النصوص', visible: true, color: '#FF9800' },
            measurements: { name: t.measurementsLayer || 'طبقة القياسات', visible: true, color: '#9b59b6' }
          },
          selectedLayer: 'layer1',
          history: [[]],
          historyIndex: 0,
          zoom: 1,
          pan: { x: 0, y: 0 }
        }
      },
      pageOrder: ['floor', 'ceiling'],
      activePageId: 'floor',
      snapToGrid: true,
      gridSize: 5,
      selectedAngleDirection: 'horizontal_right_up',
      projectInfo: {
        projectNumber: '',
        clientName: '',
        address: '',
        phone: '',
        measuredBy: '',
        branch: ''
      },
      devices: [],
      deviceNote: '',
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    // حفظ البيانات الافتراضية للمشروع الجديد
    localStorage.setItem(`cad_project:${project.id}`, JSON.stringify(defaultProjectData));
    
    setCurrentProject(project);
    
    // إعادة توجيه المستخدم إلى منطقة الرسم
    window.location.href = '/sketch';
  }, []);

  // تحميل مشروع موجود
  const handleProjectLoad = useCallback((projectData) => {
    console.log('تم تحميل المشروع:', projectData);
    
    // تحديث الصفحة لضمان تحميل البيانات الجديدة
    setTimeout(() => {
      window.location.href = '/sketch';
    }, 100);
  }, []);

  // حفظ المشروع الحالي
  const handleProjectSave = useCallback((projectId) => {
    // سيتم تنفيذ الحفظ من خلال FloorPage
    console.log('حفظ المشروع:', projectId);
  }, []);

  // حذف مشروع
  const handleProjectDelete = useCallback(() => {
    setCurrentProject(null);
    console.log('تم حذف المشروع');
  }, []);

  // تصدير مشروع
  const handleProjectExport = useCallback((exportData) => {
    console.log('تم تصدير المشروع:', exportData);
  }, []);

  // استيراد مشروع
  const handleProjectImport = useCallback((importedData) => {
    console.log('تم استيراد المشروع:', importedData);
    
    // إعادة توجيه المستخدم إلى منطقة الرسم
    window.location.href = '/sketch';
  }, []);

  return (
    <div className="projects-page" style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}>
      <div className="projects-page-header">
        <h1>{t.projectsCenter || 'مركز إدارة المشاريع'}</h1>
        <p>{t.createManageExport || 'أنشئ وادر وصدّر مشاريعك بسهولة'}</p>
      </div>
      
      <ProjectManager
        onProjectCreate={handleProjectCreate}
        onProjectLoad={handleProjectLoad}
        onProjectSave={handleProjectSave}
        onProjectDelete={handleProjectDelete}
        onProjectExport={handleProjectExport}
        onProjectImport={handleProjectImport}
        currentProject={currentProject}
        language={language}
        translations={t}
      />
      
      <div className="projects-page-footer">
        <div className="help-section">
          <h3>{t.howToUse || 'كيفية الاستخدام:'}</h3>
          <ul>
            <li><strong>{t.newProject || 'مشروع جديد'}:</strong> {t.newProjectDesc || 'إنشاء مشروع جديد بلوحة رسم فارغة'}</li>
            <li><strong>{t.load || 'تحميل'}:</strong> {t.loadDesc || 'فتح مشروع محفوظ والعمل عليه'}</li>
            <li><strong>{t.save || 'حفظ'}:</strong> {t.saveDesc || 'حفظ التقدم الحالي في المشروع'}</li>
            <li><strong>{t.export || 'تصدير'}:</strong> {t.exportDesc || 'تصدير المشروع كملف .cadproject للمشاركة أو النسخ الاحتياطي'}</li>
            <li><strong>{t.importProject || 'استيراد'}:</strong> {t.importDesc || 'استيراد مشروع من ملف .cadproject'}</li>
            <li><strong>{t.delete || 'حذف'}:</strong> {t.deleteDesc || 'حذف مشروع نهائياً من النظام'}</li>
          </ul>
        </div>
        
        <div className="features-section">
          <h3>{t.systemFeatures || 'ميزات النظام:'}</h3>
          <ul>
            <li>{t.autoSave || 'حفظ تلقائي لجميع البيانات والأشكال المرسومة'}</li>
            <li>{t.multiplePages || 'دعم صفحات متعددة (أرضية، سقف، جدران)'}</li>
            <li>{t.advancedLayers || 'نظام طبقات متقدم'}</li>
            <li>{t.saveProjectSettings || 'حفظ إعدادات المشروع ومعلومات العميل'}</li>
            <li>{t.exportImportJSON || 'تصدير واستيراد المشاريع بصيغة JSON'}</li>
            <li>{t.easyInterface || 'واجهة سهلة الاستخدام باللغة العربية'}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;